/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search, X, CheckCircle2, User, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import api from '@/lib/api';

/**
 * Compact search-picker that returns a patient the pro is allowed to link.
 * Falls back to a free-text label when the user wants to attach a note to
 * someone not in the roster (e.g. a walk-in during a home visit).
 *
 * Props
 *   endpoint          e.g. '/voice-diary/patients'
 *   value             { id: string|null, label: string }
 *   onChange          (next) => void  where next = { id, label }
 *   placeholder       string          input placeholder
 *   testIdPrefix      string          testid namespace
 */
export const PatientPicker = ({
  endpoint,
  value = { id: null, label: '' },
  onChange,
  placeholder = 'Cerca paziente per nome o email',
  testIdPrefix = 'patient-picker',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  const runSearch = useCallback(async (q) => {
    setLoading(true);
    try {
      const res = await api.get(endpoint, { params: { q: q || undefined, limit: 20 } });
      setItems(res.data?.items || []);
    } catch (e) {
      setItems([]);
    } finally { setLoading(false); }
  }, [endpoint]);

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(query), 220);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [open, query, runSearch]);

  useEffect(() => { if (open) runSearch(''); }, [open, runSearch]);

  const select = (patient) => {
    onChange?.({ id: patient.id, label: patient.full_name });
    setOpen(false);
  };

  const clear = () => {
    onChange?.({ id: null, label: '' });
    setQuery('');
  };

  const setManualLabel = (v) => {
    onChange?.({ id: null, label: v });
  };

  const linked = !!value?.id;

  return (
    <div className="space-y-1.5" data-testid={testIdPrefix}>
      <div className="flex gap-1.5">
        <div className="flex-1 relative">
          <Input
            value={value?.label || ''}
            onChange={(e) => setManualLabel(e.target.value.slice(0, 120))}
            placeholder={placeholder}
            className={cn('pr-8', linked && 'border-primary/50 ring-1 ring-primary/40')}
            maxLength={120}
            data-testid={`${testIdPrefix}-input`}
          />
          {(value?.label || linked) && (
            <button
              type="button"
              onClick={clear}
              className="absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded-full text-muted-foreground hover:bg-muted"
              aria-label="Cancella"
              data-testid={`${testIdPrefix}-clear`}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button type="button" variant="outline" size="sm" data-testid={`${testIdPrefix}-open`}>
              <Search className="h-3.5 w-3.5 mr-1" /> Cerca
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[320px] p-0" align="end">
            <div className="p-2 border-b border-border">
              <Input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Nome o email…"
                data-testid={`${testIdPrefix}-search`}
              />
            </div>
            <div className="max-h-[300px] overflow-y-auto">
              {loading && (
                <div className="p-4 text-center text-xs text-muted-foreground flex items-center justify-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" /> Caricamento…
                </div>
              )}
              {!loading && items.length === 0 && (
                <div className="p-4 text-center text-xs text-muted-foreground">
                  Nessun paziente trovato. Puoi comunque digitare un\'etichetta libera.
                </div>
              )}
              {!loading && items.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className="w-full text-left p-2 hover:bg-muted flex items-center gap-2 text-sm"
                  onClick={() => select(p)}
                  data-testid={`${testIdPrefix}-item-${p.id}`}
                >
                  <User className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="font-medium truncate">{p.full_name || '—'}</div>
                    <div className="text-[11px] text-muted-foreground truncate">{p.email}</div>
                  </div>
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      </div>
      {linked && (
        <div className="flex items-center gap-1 text-[11px] text-primary">
          <CheckCircle2 className="h-3 w-3" />
          Collegato al fascicolo del paziente
          <Badge variant="outline" className="ml-1 font-mono text-[9px]">{String(value.id).slice(0, 8)}…</Badge>
        </div>
      )}
    </div>
  );
};

export default PatientPicker;
